import type { BroadcastMessage } from '../types'
import { useCoreConfig, computed, useState, createSharedComposable, onUnmounted } from '#imports'

type MessageCallback = (message: BroadcastMessage) => void

export const useSharedBroadcast = createSharedComposable(() => {
    if (import.meta.server) {
        const sendMessage = (message: BroadcastMessage) => {}
        const onMessage = (callback: MessageCallback): void => {
            return
        }

        return {
            sendMessage,
            onMessage,
            isConnected: false,
        }
    }

    const { site } = useCoreConfig()

    const broadcaster = useState<BroadcastChannel | undefined>('shared-broadcast-channel', () => undefined)
    const receiver = useState<BroadcastChannel | undefined>('shared-broadcast-receiver', () => undefined)
    const listeners = useState<Set<MessageCallback>>('shared-broadcast-listeners', () => new Set<MessageCallback>())

    const isConnected = computed(() => Boolean(broadcaster.value))

    const attachBroadcaster = () => {
        if (broadcaster.value) return

        broadcaster.value = new BroadcastChannel(`${site.brand}-broadcast-channel`)
        receiver.value = new BroadcastChannel(`${site.brand}-broadcast-channel`)

        receiver.value.onmessage = (event: MessageEvent) => {
            try {
                const message: BroadcastMessage = event.data
                listeners.value.forEach(callback => callback(message))
            }
            catch {
                // Silently ignore error
            }
        }
    }

    const sendMessage = (message: BroadcastMessage) => {
        if (!broadcaster.value) {
            attachBroadcaster()
        }

        broadcaster.value?.postMessage(message)
    }

    const onMessage = (callback: MessageCallback): void => {
        listeners.value.add(callback)

        const removeListener = () => {
            listeners.value.delete(callback)
        }

        onUnmounted(() => {
            removeListener()
        })
    }

    attachBroadcaster()

    return {
        sendMessage,
        onMessage,
        isConnected,
    }
})
